import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
  signOut,
  type AuthError,
  type User,
} from 'firebase/auth'
import React, { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { auth } from '../../firebase'

import LoadingLayout from '../../components/layout/LoadingLayout'
import { useModal } from '../modal/useModal'
import { AuthContext, type AuthContextType } from './authContext'

interface AuthProviderProps {
  children: ReactNode
}

const INACTIVITY_KEY = 'lastActivityTime'

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { showModal } = useModal()

  const setLastActivityTime = (time: number) => {
    console.log('Actividad')
    localStorage.setItem(INACTIVITY_KEY, time.toString())
  }

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  const signInWithGoogle = useCallback(async () => {
    try {
      setError(null)

      const provider = new GoogleAuthProvider()
      const userCredential = await signInWithPopup(auth, provider)

      setUser(userCredential.user)

      return userCredential
    } catch (err) {
      const authError = err as AuthError
      setError(getAuthErrorMessage(authError.code))
      throw err
    }
  }, [])

  const logout = useCallback(async () => {
    try {
      setError(null)
      await signOut(auth)
      setUser(null)
    } catch (err) {
      const authError = err as AuthError
      setError(getAuthErrorMessage(authError.code))
      throw err
    }
  }, [])

  let isFirstRender = true
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (isFirstRender) {
        setUser(user)
        isFirstRender = false
      }
      setLoading(false)
    })

    return unsubscribe
  }, [])

  useEffect(() => {
    if (!user) return

    let timeoutId: NodeJS.Timeout

    const updateActivity = () => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(() => {
        setLastActivityTime(Date.now())
      }, 200) // Debounce de 0.2 segundos
    }

    const events = ['mousedown', 'keydown', 'touchstart', 'click', 'mousemove']
    events.forEach((event) => {
      window.addEventListener(event, updateActivity, { passive: true })
    })

    window.addEventListener('scroll', updateActivity, { passive: true })
    document.addEventListener('scroll', updateActivity, { passive: true })

    return () => {
      clearTimeout(timeoutId)
      events.forEach((event) => {
        window.removeEventListener(event, updateActivity)
      })
      window.removeEventListener('scroll', updateActivity)
      document.removeEventListener('scroll', updateActivity)
    }
  }, [user])

  useEffect(() => {
    if (!user) return

    const SESSION_TIMEOUT = 1 * 60 * 60 * 1000 // 1 hour in milliseconds

    const checkSessionExpiration = () => {
      const lastActivityTime = localStorage.getItem(INACTIVITY_KEY)
        ? parseInt(localStorage.getItem(INACTIVITY_KEY) as string, 10)
        : Date.now()

      const currentTime = Date.now()
      const timeSinceLastActivity = currentTime - lastActivityTime

      console.log({ timeSinceLastActivity, SESSION_TIMEOUT })
      if (timeSinceLastActivity >= SESSION_TIMEOUT) {
        void logout()
        showModal({
          type: 'warning',
          title: 'Sesión expirada',
          message: 'Tu sesión ha expirado por inactividad. Por favor, vuelve a iniciar sesión.',
          confirmText: 'Iniciar sesión',
          showCancel: false,
          closeOnOverlayClick: false,
          onConfirm: () => {
            window.location.replace('/')
          },
        })
      }
    }

    const intervalId = setInterval(checkSessionExpiration, 100000) // Verificar cada 1 minuto

    return () => clearInterval(intervalId)
  }, [user, logout, showModal])

  const value: AuthContextType = useMemo(
    () => ({
      user,
      loading,
      signInWithGoogle,
      logout,
      error,
      clearError,
    }),
    [user, loading, signInWithGoogle, logout, error, clearError]
  )
  if (loading) {
    return <LoadingLayout />
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

function getAuthErrorMessage(errorCode: string): string {
  switch (errorCode) {
    case 'auth/user-not-found':
      return 'No se encontró un usuario con este correo electrónico.'
    case 'auth/wrong-password':
      return 'Contraseña incorrecta.'
    case 'auth/invalid-email':
      return 'Dirección de correo electrónico inválida.'
    case 'auth/user-disabled':
      return 'Esta cuenta ha sido deshabilitada.'
    case 'auth/too-many-requests':
      return 'Demasiados intentos fallidos. Intenta de nuevo más tarde.'
    case 'auth/invalid-credential':
      return 'Correo o contraseña inválidos.'
    case 'auth/popup-closed-by-user':
      return 'La ventana de Google se cerró antes de completar el inicio de sesión.'
    case 'auth/cancelled-popup-request':
      return 'Solo se permite una ventana de inicio de sesión a la vez.'
    case 'auth/popup-blocked':
      return 'La ventana emergente fue bloqueada por el navegador.'
    case 'auth/account-exists-with-different-credential':
      return 'Ya existe una cuenta con este correo electrónico pero con diferente método de inicio de sesión.'
    case 'auth/missing-email':
      return 'Por favor, ingresa tu correo electrónico.'
    default:
      return 'Ocurrió un error durante la autenticación.'
  }
}
